import { useResolutionInfo } from '@mm/hooks';
import audiencesCRUDActions, {
  useAudiencesCRUDActions,
} from '@mm/state/actions/AudiencesCRUDActions';
import { selectInvalidAudienceContactRecordsIds } from '@mm/state/selectors/contactsSelector';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { Pagination, StoreState } from '@mm/state/store-interfaces';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

export const useFetchAudienceContacts = ({
  activeAudienceId,
  isElasticBasedAudience,
  options,
}: {
  activeAudienceId?: number;
  isElasticBasedAudience?: boolean;
  options?: {
    pageSize?: number;
    skipBusy?: boolean;
    pauseRefreshingWhenEditingAudience?: boolean;
    withDeliverable?: boolean;
    sortColumn?: 'last_session' | 'created_at';
    sortDirection?: 'asc' | 'desc';
  };
}) => {
  const { pageSize, skipBusy, withDeliverable, sortColumn, sortDirection } =
    options || {};

  const dispatch: Dispatch<StoreState> = useDispatch();
  const [isFetching, setIsFetching] = useState(false);

  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const activeFacebookPageId = useSelector(activeFacebookPageIdSelector);
  const invalidIds = useSelector(selectInvalidAudienceContactRecordsIds);
  const currentAudienceInvalid = invalidIds.includes(Number(activeAudienceId));
  const editingAudience = useSelector((state: StoreState) =>
    state.view.slideoutMenu.some(
      menu =>
        menu.menuType === 'editAudience' &&
        menu.menuProps?.audienceId === activeAudienceId,
    ),
  );
  const allowUpdateOfInvalidAudience =
    !options?.pauseRefreshingWhenEditingAudience || !editingAudience;

  const { getContactsById } = useAudiencesCRUDActions();
  const MOBILE_THRESHOLD = 1024;
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;

  const fetch = useCallback(
    (options?: { ignoreErrors?: boolean }) => {
      const get = async () => {
        if (activeAudienceId) {
          setIsFetching(true);

          const optionsToPass: Parameters<
            typeof getContactsById
          >[0]['options'] = {
            pageSize: pageSize ?? 25,
            skipBusy,
            throwErrors: options?.ignoreErrors,
            withDeliverable,
          };
          if (isElasticBasedAudience) {
            if (sortColumn) {
              optionsToPass.sortColumn = sortColumn;
            }
            if (sortDirection) {
              optionsToPass.sortDirection = sortDirection;
            }
          }

          try {
            await getContactsById({
              botId: activeChatbotId,
              audienceId: activeAudienceId,
              facebookPageId: activeFacebookPageId,
              options: optionsToPass,
            });
          } catch {
            // Ignore
          }

          setIsFetching(false);
        }
      };
      get();
    },
    [
      activeAudienceId,
      activeChatbotId,
      activeFacebookPageId,
      getContactsById,
      pageSize,
      skipBusy,
      sortColumn,
      sortDirection,
      withDeliverable,
      isElasticBasedAudience,
    ],
  );

  const mounted = useRef({});
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  });

  // fetch first time
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    () => fetch({ ignoreErrors: true }),
    [activeAudienceId, sortDirection],
  );

  // re-fetch on invalidation
  useEffect(() => {
    if (
      mounted.current &&
      activeAudienceId &&
      currentAudienceInvalid &&
      allowUpdateOfInvalidAudience &&
      !isFetching
    ) {
      fetch();
    }
  }, [
    activeAudienceId,
    allowUpdateOfInvalidAudience,
    currentAudienceInvalid,
    editingAudience,
    fetch,
    isFetching,
    sortDirection,
  ]);

  const fetchMore = async (pagination?: Pagination) => {
    if (pagination && !pagination.isLastPage && activeAudienceId && !isMobile) {
      setIsFetching(true);
      const optionsToPass: Parameters<typeof getContactsById>[0]['options'] = {
        pageNumber: pagination.page + 1,
        pageSize: pagination.perPage,
        skipBusy,
        withDeliverable,
      };

      if (isElasticBasedAudience) {
        if (sortColumn) {
          optionsToPass.sortColumn = sortColumn;
        }
        if (sortDirection) {
          optionsToPass.sortDirection = sortDirection;
        }
      }
      await getContactsById({
        botId: activeChatbotId,
        audienceId: activeAudienceId,
        facebookPageId: activeFacebookPageId,
        options: optionsToPass,
      });
      setIsFetching(false);
    }
  };

  const fetchPage = async ({
    pageSize,
    pageNumber,
    query,
  }: {
    pageSize?: number;
    pageNumber?: number;
    query?: string;
  }) => {
    if (pageSize && activeAudienceId) {
      const optionsToPass: Parameters<typeof getContactsById>[0]['options'] = {
        pageNumber,
        pageSize,
        query,
        skipBusy,
        showPageContacts: true,
        withDeliverable,
      };

      if (isElasticBasedAudience) {
        if (sortColumn) {
          optionsToPass.sortColumn = sortColumn;
        }
        if (sortDirection) {
          optionsToPass.sortDirection = sortDirection;
        }
      }
      setIsFetching(true);
      await getContactsById({
        botId: activeChatbotId,
        audienceId: activeAudienceId,
        facebookPageId: activeFacebookPageId,
        options: optionsToPass,
      });
      setIsFetching(false);
    }
  };

  const invalidate = () => {
    dispatch({
      type: audiencesCRUDActions.updateAction,
      entity: { id: activeAudienceId },
    });
  };

  return {
    fetchMore,
    fetchPage,
    invalidate,
    isFetching,
  };
};
